import { cn, LocalizedDateTime, Row } from '@rematter/ui-web';

import { NotificationFeedEntry } from '~/modules/notifications/types';
import { getNotificationIconByKey } from '~/modules/notifications/utils/notificationTypeIcons';

interface NotificationItemProps {
  entry: NotificationFeedEntry;
  onMarkAsRead: (id: string) => void;
  onNavigate: (actionLink: string) => void;
}

export const NotificationItem = ({
  entry,
  onMarkAsRead,
  onNavigate,
}: NotificationItemProps) => {
  const Icon = getNotificationIconByKey(entry.notification.notificationTypeKey);

  const handleClick = () => {
    if (!entry.isRead) {
      onMarkAsRead(entry.id);
    }
    if (entry.notification.actionLink) {
      onNavigate(entry.notification.actionLink);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${entry.isRead ? 'Read' : 'Unread'}: ${entry.notification.title}`}
      className={cn(
        '@NotificationItem',
        'flex w-full cursor-pointer gap-3 border-b border-neutral/10 px-4 py-3 text-left transition-colors',
        'hover:bg-neutral/5',
        !entry.isRead && 'bg-primary/5',
      )}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="relative mt-0.5 flex-shrink-0">
        <div
          className={cn(
            'flex size-8 items-center justify-center rounded-full',
            entry.isRead ? 'bg-neutral/5' : 'bg-primary/10',
          )}
        >
          <Icon
            className={cn(
              'size-4',
              entry.isRead ? 'text-neutral/40' : 'text-primary',
            )}
          />
        </div>
        {!entry.isRead && (
          <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-white bg-primary" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <Row xBetween yCenter>
          <span
            className={cn(
              'truncate text-sm',
              !entry.isRead ? 'font-semibold text-neutral' : 'text-neutral/70',
            )}
          >
            {entry.notification.title}
          </span>
          <span className="ml-2 flex-shrink-0 text-xs text-neutral/50">
            <LocalizedDateTime
              date={entry.createdAt}
              dateStyle="relative-within-week"
            />
          </span>
        </Row>
        <p className="mt-0.5 truncate text-xs text-neutral/60">
          {entry.notification.body}
        </p>
      </div>
    </div>
  );
};
